import {
  MouseEventHandler,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';

import { debounce } from '../01-core/Utilities';
import { OptionalLink, OptionalLinkProps } from '../01-core/OptionalLink';
import { Swipeable } from '../01-core/Swipeable';
import { Icon } from '../02-atoms/Icon';
import { SliderNavigation } from '../02-atoms/SliderNavigation';
import { Card, CardProps } from '../03-molecules/Card';

export interface ListingHeaderItemProps extends OptionalLinkProps {
  /** The label */
  label: string;
  /** Whether the item triggers sorting */
  sortable?: boolean;
}

export interface ListingProps {
  /** Type of card */
  type: CardProps['type'];
  /** Style of card */
  style?: CardProps['style'];
  /** Card objects */
  items: CardProps[];
  /** Slider */
  slider?: boolean;
  /** Optional label for previous page */
  prevLabel?: string;
  /** Optional label for next page */
  nextLabel?: string;
  /** Optional label for swipe indicator */
  swipeLabel?: string;
  /** Current page */
  currentPage?: number | null;
  /** List only (no columns) */
  list?: boolean;
  /** Optional header items */
  header?: ListingHeaderItemProps[];
  /** Change page handler */
  onChange?: (tab: number) => void;
  /** Optional capture phase click handler */
  onClickCapture?: MouseEventHandler<HTMLDivElement>;
  /** Additional class */
  className?: string;
  /** Optional card component */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  card?: any;
}

const getCardKey = (item: CardProps, index: number) => {
  // `uid` carries the content's own identity. `link` must not be used here:
  // every Occasion of an event carries the link of its PARENT event, so several
  // cards in one listing would end up with the same key.
  if (item.uid) {
    return item.uid;
  }

  const title = typeof item.title === 'string' ? item.title : '';
  return `${item.type}-${title}-${index}`;
};

/** Block rendering multiple cards of various types */
export const Listing = ({
  type,
  items,
  slider,
  currentPage = null,
  prevLabel,
  nextLabel,
  swipeLabel,
  list,
  header,
  onChange,
  card = null,
  style,
  className,
  ...props
}: ListingProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const tape = useRef<HTMLDivElement | null>(null);
  const [pages, setPages] = useState([]);
  const [pageOffsets, setPageOffsets] = useState([0]);
  const [page, setPage] = useState(0);
  const CardComponent = card ?? Card;

  const calculatePages = useCallback(() => {
    if (!tape.current || !ref.current) return;
    const calculated = [];
    const offsets = [];
    const width = ref.current.getBoundingClientRect().width;
    const diff = tape.current.getBoundingClientRect().left;
    let max = width;
    let current = 1;
    const itemElements = tape.current.querySelectorAll('.listing__item');
    calculated.push(current);
    offsets.push(0);
    items.forEach((_, index) => {
      const rect = itemElements[index].getBoundingClientRect();
      if (rect.right - diff > max && rect.left - diff > 0) {
        current = current + 1;
        calculated.push(current);
        offsets.push(rect.left - diff);
        max = rect.left + width - diff;
      }
      if (index == itemElements.length - 1) {
        offsets[offsets.length - 1] = rect.right - diff - width;
      }
    });
    setPages(calculated as []);
    setPageOffsets(offsets);
    tape.current.style.setProperty('--offset', -offsets[page] + 'px');
  }, [tape, ref, items, page]);

  const swipeTo = (direction: string) => {
    if (direction == 'next') {
      slideTo(Math.min(pages.length - 1, page + 1));
    } else if (direction == 'prev') {
      slideTo(Math.max(0, page - 1));
    }
  };

  const slideTo = (index: number) => {
    if (!tape.current || !ref.current) return;
    setPage(index);
    tape.current.style.setProperty('--offset', -pageOffsets[index] + 'px');

    if (onChange) {
      onChange(index);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(
    debounce(
      () => {
        calculatePages();
      },
      100,
      false,
    ),
    [calculatePages],
  );

  useEffect(() => {
    if (!slider) {
      return;
    }
    window.addEventListener('resize', handleResize);
    window.requestAnimationFrame(() => {
      calculatePages();

      // State managed by parent
      if (currentPage !== null && currentPage !== page) {
        setPage(currentPage);
      }
    });
    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleResize, calculatePages, slider]);

  list = list || style === 'table';

  return (
    <Swipeable
      ref={ref}
      onSwipeLeft={slider ? () => swipeTo('next') : undefined}
      onSwipeRight={slider ? () => swipeTo('prev') : undefined}
      className={[
        'listing',
        'listing--' + type,
        slider ? 'listing--slider' : '',
        list ? 'listing--list' : '',
        style ? `listing--${style}` : '',
        className,
      ].join(' ')}
      {...props}
    >
      {header && items.length > 0 && (
        <div className="listing__header">
          {header.map((item, i) => (
            <OptionalLink
              key={i}
              div={true}
              className="listing__header-item"
              onClick={item.onClick}
            >
              {item.label}
              {item.sortable && <Icon icon="arrows_sort" />}
            </OptionalLink>
          ))}
        </div>
      )}
      <div ref={tape} className="listing__contents">
        {items.map((item, key) => {
          return (
            <CardComponent
              key={getCardKey(item, key)}
              className="listing__item"
              style={style}
              {...item}
            />
          );
        })}
      </div>
      {slider && pages.length > 1 && (
        <SliderNavigation
          pages={pages}
          page={page}
          swipe={true}
          onChange={slideTo}
          prevLabel={prevLabel}
          nextLabel={nextLabel}
          swipeLabel={swipeLabel}
          className="listing__navigation"
        />
      )}
    </Swipeable>
  );
};
